import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { logger, initGlobalErrorLogging, markLpEnter } from './common/utils/logger';

// First thing this bundle does: stamp "the visitor landed on the LP" with
// the full entry params/referrer/user-agent, and start the clock that
// lp_session/exit (fired on tab-hide/close) measures total time-on-LP from.
markLpEnter();
initGlobalErrorLogging();

const waitForAntiFraud = async () => {
  const op = logger.startOperation("app_bootstrap", "waitForAntiFraud");

  try {
    if (window.__ANTIFRAUD_READY__) {
      await window.__ANTIFRAUD_READY__;
    }
    op.success("Anti-fraud check finished — we now know which carrier network (if any) this visitor is on");
  } catch (e) {
    op.fail(e, "Anti-fraud check crashed before it could tell us which carrier network this visitor is on");
  }

  return {
    zainDcb: window.__ZAIN_PROTECT__ || { ti: null, ts: null, type: null },
    // __ANTIFRAUD_NETWORK__ is set by public/index.html's initAntiFraud
    // (never "ASIACELL" for a visitor on Zain's network — Zain is checked
    // first there) — reused here rather than adding an AsiaCell-only global.
    isAsiaCellHe: window.__ANTIFRAUD_NETWORK__ === "ASIACELL",
  };
};

/**
 * Mounted immediately (no async work blocks the initial `root.render` call
 * below), so the visitor sees the spinner markup the instant the bundle
 * parses instead of a blank white page for however long anti-fraud/campaign
 * lookup take. Swaps to <App> once both resolve.
 *
 * This does NOT weaken the anti-fraud gating anywhere: the header flow
 * (Zain one-click subscribe) only ever mounts once `ti`/`ts` are known,
 * since <App> itself doesn't render until then — and the PIN flow's own
 * Verify button is separately gated on its own msisdn-driven anti-fraud
 * check inside usePinFlow, untouched by this.
 */
function Bootstrap() {
  const [appProps, setAppProps] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      logger.info("app_bootstrap", "init", "App is starting up for this visitor");

      const { zainDcb, isAsiaCellHe } = await waitForAntiFraud();

      // public/index.html's bootstrap script already kicked off this same
      // campaign lookup (needed there to decide whether to redirect
      // off-network visitors to the PIN route before this bundle even
      // loads) — reuse its result instead of firing a second, duplicate
      // request.
      let res = null;
      const campaignOp = logger.startOperation("app_bootstrap", "campaignLookup");
      try {
        if (window.__CAMPAIGN_READY__) {
          res = await window.__CAMPAIGN_READY__;
        }
        campaignOp.success("Figured out which campaign/LP this visitor belongs to", { campaignCode: res?.code ?? null });
      } catch (e) {
        campaignOp.fail(e, "Could not determine which campaign/LP this visitor belongs to");
      }

      if (cancelled) return;

      setAppProps({
        ti: zainDcb.ti,
        ts: zainDcb.ts,
        camp: res?.code,
        isAsiaCellHe,
      });

      logger.info("app_bootstrap", "init", "App loaded and rendered — the visitor can now see the LP");
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!appProps) {
    return <div className="lp-boot-spinner" role="status" aria-label="Loading" />;
  }

  return (
    <App
      ti={appProps.ti}
      ts={appProps.ts}
      camp={appProps.camp}
      isAsiaCellHe={appProps.isAsiaCellHe}
    />
  );
}

const rootEl = document.getElementById("root");

if (!rootEl) {
  logger.error("app_bootstrap", "init", "Could not start the app — the page is missing its #root element");
  logger.flush();
} else {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <Bootstrap />
    </React.StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
